import { CacheConfig } from './interfaces';

interface CacheValue {
  value: unknown;
  expiresAt: number;
}

export class CacheStorage<T> {
  private readonly cache = new Map<string, CacheValue>();
  private readonly autoCleanup: boolean;
  readonly maxAge: number;

  constructor(config: CacheConfig<T>) {
    this.maxAge = config.strategy?.maxAge || 60000;
    this.autoCleanup = config.strategy?.autoCleanup || false;
  }

  has(key: string): boolean {
    if (this.cache.has(key)) {
      const cacheValue = this.cache.get(key);
      if (cacheValue !== undefined && cacheValue.expiresAt > Date.now()) {
        return true;
      }
      this.cache.delete(key);
    }
    return false;
  }

  get(key: string, allowExpired?: boolean): unknown | undefined {
    if (this.cache.has(key)) {
      const cacheValue = this.cache.get(key);
      if (cacheValue !== undefined && (allowExpired || cacheValue.expiresAt > Date.now())) {
        return cacheValue.value;
      }
      this.cache.delete(key);
    }
    return undefined;
  }

  set(key: string, value: unknown): void {
    const expiresAt = Date.now() + this.maxAge;
    const cacheValue = { value, expiresAt };
    this.cache.set(key, cacheValue);

    if (this.autoCleanup) {
      setTimeout(() => this.delete(key), this.maxAge);
    }
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  toObject(): { [k: string]: unknown } {
    return Object.fromEntries(this.cache);
  }
}
